/**
 * NotificationBell - bell icon with unread badge + dropdown list
 * Shows new messages, photo likes, follows. Reachable from top bars.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageCircle, UserPlus, Music, Loader2, CheckCheck, Camera } from 'lucide-react';
import { notificationsApi } from '../api/notifications';
import { useAuth } from '../context/AuthContext';

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

const iconFor = (type) => {
  switch (type) {
    case 'like': return <Heart className="w-4 h-4 text-[#FF007F]" />;
    case 'new_message':
    case 'message': return <MessageCircle className="w-4 h-4 text-[#00F0FF]" />;
    case 'follow': return <UserPlus className="w-4 h-4 text-purple-400" />;
    case 'event_photos': return <Camera className="w-4 h-4 text-[#39FF14]" />;
    default: return <Music className="w-4 h-4 text-white/70" />;
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const data = await notificationsApi.getMy(30);
      setItems(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch (err) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotifs]);

  // Close dropdown on outside click (bell toggle only; the portaled panel uses its own backdrop)
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (!open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await fetchNotifs();
      setLoading(false);
    }
  };

  const handleClickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setUnread(u => Math.max(0, u - 1));
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      notificationsApi.markRead([n.id]).catch(() => {});
    }
    const url = n.metadata?.link_url;
    if (url) navigate(url);
  };

  const handleMarkAll = async () => {
    setUnread(0);
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    try { await notificationsApi.markAllRead(); } catch (err) { /* ignore */ }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-white" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-[#FF007F] rounded-full flex items-center justify-center text-white text-xs font-bold"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[99]"
            onClick={() => setOpen(false)}
            data-testid="notification-backdrop"
          />
          <div
            className="fixed top-16 left-2 right-2 sm:left-auto sm:right-4 sm:w-80 bg-[#12101A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[100] flex flex-col max-h-[75vh]"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
              <span className="text-white font-semibold text-sm">Notifications</span>
              {unread > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="flex items-center gap-1 text-[#00F0FF] text-xs font-medium hover:opacity-80"
                  data-testid="notification-mark-all-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-[#FF007F]" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <Bell className="w-10 h-10 mx-auto mb-3 text-[#3F3F46]" />
                  <p className="text-[#71717A] text-sm">No notifications yet</p>
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleClickItem(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 ${!n.read ? 'bg-[#FF007F]/5' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">
                      {n.fromUserPhoto ? (
                        <img src={n.fromUserPhoto} alt="" className="w-full h-full object-cover" />
                      ) : (
                        iconFor(n.type)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm leading-snug break-words">{n.message}</p>
                      <span className="text-[#71717A] text-xs">{timeAgo(n.createdAt)}</span>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF007F] flex-shrink-0 mt-2" />}
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full py-3 text-center text-[#00F0FF] text-sm font-medium hover:bg-white/5 transition-colors border-t border-white/10 flex-shrink-0"
              data-testid="notification-see-all-btn"
            >
              See all
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
